import {
  EuiPageTemplate,
  EuiSkeletonText,
  EuiTitle,
} from "@inscopix/ideas-eui";
import { Tenant } from "graphql/_Types";
import { RefObject, useMemo } from "react";
import { CallOutError } from "components/CallOutError/CallOutError";
import { useGetProjects } from "hooks/useGetProjects";
import { ProjectCards } from "components/ProjectsCards/ProjectCards";
import { useUserContext } from "providers/UserProvider/UserProvider";
import { ideasFeatures } from "ideas.features";

interface PanelProjectsProps {
  tenantId?: Tenant["id"];
  virtuaScrollRef: RefObject<HTMLElement>;
}

export const PanelProjects = ({
  tenantId,
  virtuaScrollRef,
}: PanelProjectsProps) => {
  const hasPersonalSpaceOrTenant = useUserContext(
    (s) =>
      s.personalSpace !== undefined ||
      s.tenants.length > 0 ||
      s.externalTenants.length > 0,
  );

  const { projects, loading } = useGetProjects({ tenantId });

  const projectsBody = useMemo(() => {
    if (loading) {
      return <EuiSkeletonText lines={5} />;
    }

    if (projects === undefined) {
      return <CallOutError />;
    }

    return (
      <ProjectCards
        projects={projects}
        tenantId={tenantId}
        virtuaScrollRef={virtuaScrollRef}
      />
    );
  }, [loading, projects, tenantId, virtuaScrollRef]);

  // If there are no projects and no personal space or tenant to create a project in, just show a welcome message
  if (
    ideasFeatures.personalSpace &&
    !hasPersonalSpaceOrTenant &&
    projects?.length === 0
  ) {
    return (
      <EuiPageTemplate.Section
        grow={false}
        restrictWidth={false}
        style={{ minWidth: 500 }}
      >
        <EuiTitle>
          <h1>Welcome to IDEAS</h1>
        </EuiTitle>
      </EuiPageTemplate.Section>
    );
  }

  return (
    <EuiPageTemplate.Section
      grow
      restrictWidth={false}
      contentProps={{ style: { height: "100%" } }}
      style={{ minWidth: 500 }}
    >
      {projectsBody}
    </EuiPageTemplate.Section>
  );
};
